import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useBreakpoint, useGoogleMapsPlacesBff } from '@panel/commons';
import { isKeyCodeNumber, isKeyCodeNumpadNumber, isSpecialKey } from '@panel/commons/helpers/keyUtils';
import { getDevice, site } from '@panel/commons/helpers';
import { locationStepSelectors, locationStepOperations } from '../../../ducks/location';
import { InfoMessage } from './styles';
import {
  addressSeparator,
  countriesIds,
  formatCep,
  getPostalCodeLongName,
  hasCep,
  isAnExcludePortaltoGetLatLng,
  isValidFormatCep
} from './utils';
import LocationCombosCommon from './LocationCombosCommon';
import LocationCombosBR from './LocationCombosBR';
const siteId = site.id;

export const LocationSection = ({
  register,
  errors,
  isLoaded,
  loadError,
  setValue,
  getValues,
  clearErrors,
  trigger,
  reset,
  watch,
  isOnCEPView,
  setIsOnCEPV,
  setHasSearchWithCep,
  lastCepSearched,
  setLastCepSearched,
  isEdition,
  hasDataSavedOnDB,
  showErrorSnackbar,
  hasSearchWithCep
}) => {
  const [addressValidation, setAddressValidation] = useState(null);
  const [addressValue, setAddressValue] = useState('');
  const [getSuggestions, setGetSuggestions] = useState(true);
  const [siteCodes, setSiteCodes] = useState({});
  const [searchWithCep, setSearchWithCep] = useState(false);
  const {
    isEdited,
    hasCoordinates,
    formValues,
    cep,
    cepError,
    cepNotFound,
    loadingCep,
    loadingDistrict,
    addressComplement
  } = useSelector(locationStepSelectors.getLocationStepState);
  const [breakMobile] = useBreakpoint(480);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const placesOptions = useMemo(
    () => ({
      componentRestrictions: siteCodes.useGmapCode ? { country: siteCodes.useGmapCode } : undefined,
      isScriptLoaded: isLoaded
    }),
    [siteCodes.useGmapCode, isLoaded]
  );
  const {
    ready: placesReady,
    loading: placesLoading,
    predictions,
    search: searchPlaces,
    getDetails,
    geocode,
    resetSession
  } = useGoogleMapsPlacesBff(placesOptions);
  const {
    address,
    selectedProvince,
    selectedCity,
    selectedDistrict,
    selectedSubDistrict,
    provinceData,
    cityData,
    districtData,
    subDistrictData,
    locationInfo,
    postalCode
  } = formValues;
  const cepvalue = watch('cep');
  const cepDefault = cepvalue ? formatCep(cepvalue) : formatCep(cep);
  const isMobile = getDevice() === 'mobile';
  const setIsEdited = value => {
    if (isEdited !== value) {
      dispatch(locationStepOperations.setIsEdited(value));
    }
  };

  useEffect(() => {
    // Si estamos editando y hay datos guardados en la DB ( lo logico ), pero no hay coordenadas,
    if (
      hasDataSavedOnDB &&
      isEdition &&
      locationInfo &&
      (hasCoordinates?.lng === null || hasCoordinates?.lat === null)
    ) {
      const keys = Object.keys(locationInfo);
      // Espero a que se carguen los datos de los selectores para poder ejecutar la busqueda de forma manual
      const allFieldsWithValues = keys.every(key => {
        if (key === 'idprovincia' && locationInfo[key]) {
          return !!selectedProvince;
        }
        if (key === 'idciudad' && locationInfo[key]) {
          return !!selectedCity;
        }
        if (key === 'idzonaciudad' && locationInfo[key]) {
          return !!selectedDistrict;
        }
        return false;
      });
      if (allFieldsWithValues) {
        getLocationWithoutSuggestion(selectedCity, selectedDistrict);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDataSavedOnDB, hasCoordinates, isEdition, locationInfo, selectedCity, selectedDistrict, selectedProvince]);

  useEffect(() => {
    if (countriesIds[siteId]) {
      setSiteCodes({ useCountryId: countriesIds[siteId].countryCode, useGmapCode: countriesIds[siteId].gmapCode });
      dispatch(locationStepOperations.getLocation(`V1-A-${countriesIds[siteId].countryCode}`, 'provinceData'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Registrar campos de ubicación en React Hook Form
  useEffect(() => {
    register('provinceVal');
    register('cityVal');
    register('districtVal');
    register('subDistrictVal');
    if (site.isBrasil) {
      register('cep');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register]);

  // utilizo estos efectos para mantener actualizados los valores de los campos en RHF
  useEffect(() => {
    if (selectedCity?.location_id) {
      setValue('cityVal', selectedCity.location_id);
      clearErrors('cityVal');
    } else if (selectedCity === null || selectedCity === '') {
      setValue('cityVal', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity?.location_id, setValue, clearErrors]);

  useEffect(() => {
    if (selectedProvince?.location_id) {
      setValue('provinceVal', selectedProvince.location_id);
      clearErrors('provinceVal');
    } else if (selectedProvince === null || selectedProvince === '') {
      setValue('provinceVal', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvince?.location_id, setValue, clearErrors]);

  useEffect(() => {
    if (selectedDistrict?.location_id) {
      setValue('districtVal', selectedDistrict.location_id);
      clearErrors('districtVal');
    } else if (selectedDistrict === null || selectedDistrict === '') {
      setValue('districtVal', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDistrict?.location_id, setValue, clearErrors]);

  useEffect(() => {
    if (selectedSubDistrict?.location_id) {
      setValue('subDistrictVal', selectedSubDistrict.location_id);
      clearErrors('subDistrictVal');
    } else if (selectedSubDistrict === null || selectedSubDistrict === '') {
      setValue('subDistrictVal', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubDistrict?.location_id, setValue, clearErrors]);

  useEffect(() => {
    if (address) {
      const [shortAddress] = address.split(addressSeparator);
      setAddressValue(shortAddress);
      setValue('addressVal', shortAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, isEdited, setValue]);

  useEffect(() => {
    if (addressComplement && getValues('addressComplement') !== addressComplement) {
      setValue('addressComplement', addressComplement);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressComplement]);

  useEffect(() => {
    if (locationInfo && !isEdited) {
      if (locationInfo.idprovincia) {
        const getLocationData = provinceData.find(province => province.location_id === locationInfo.idprovincia);
        dispatch(locationStepOperations.setSelectedProvince(getLocationData, null));
      }
      if (locationInfo.idciudad) {
        const getLocationData = cityData.find(city => city.location_id === locationInfo.idciudad);
        dispatch(locationStepOperations.setSelectedCity(getLocationData, null));
      }
      if (locationInfo.idzonaciudad) {
        const getLocationData = districtData?.find(district => district.location_id === locationInfo.idzonaciudad);
        dispatch(locationStepOperations.setSelectedDistrict(getLocationData, null));
      }
      if (locationInfo.idsubzonaciudad) {
        const getLocationData = subDistrictData.find(
          subdistrict => subdistrict.location_id === locationInfo.idsubzonaciudad
        );
        dispatch(locationStepOperations.setSelectedSubDistrict(getLocationData));
      }
    }
  }, [cityData, dispatch, districtData, isEdited, locationInfo, provinceData, subDistrictData]);

  const setLocationByCoords = locationValues => {
    const parsedLocationInfo = Object.keys(locationValues).reduce((nuevoObjeto, clave) => {
      nuevoObjeto[clave.toLowerCase()] = locationValues[clave];
      return nuevoObjeto;
    }, {});
    const { idprovincia, idciudad, idzonaciudad } = parsedLocationInfo;
    if (idprovincia) {
      dispatch(locationStepOperations.resetValues());
      setIsEdited(false);
      dispatch(locationStepOperations.getLocation(idprovincia, 'cityData'));
    }
    if (idciudad) {
      dispatch(locationStepOperations.getLocation(idciudad, 'districtData'));
    }
    if (idzonaciudad) {
      dispatch(locationStepOperations.getLocation(idzonaciudad, 'subDistrictData'));
    }
  };

  useEffect(() => {
    if (isLoaded && locationInfo && isEdited === false) {
      setLocationByCoords(locationInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationInfo, isLoaded, isEdited]);

  const handleOnError = (status, clearSuggestions) => {
    if (status === 'ZERO_RESULTS') {
      setAddressValidation(<InfoMessage>{t('professionalPubli.location.address.notFound')}</InfoMessage>);
      clearSuggestions();
    }
  };

  const setAddress = value => {
    dispatch(locationStepOperations.setAddress(value));
  };

  const setCep = value => {
    const formattedCep = formatCep(value);
    if (formattedCep || formattedCep === '') {
      setValue('cep', formattedCep);
      dispatch(locationStepOperations.setCep(formattedCep));
    }
  };

  const setAddressComplement = value => {
    if (value) {
      dispatch(locationStepOperations.setAddressComplement(value));
    } else if (!value && getValues('addressComplement') === '') {
      dispatch(locationStepOperations.setAddressComplement(''));
    }
  };

  const setDefaultLocation = (city, district, subDistrict) => {
    if (subDistrict && subDistrict.latitude && subDistrict.longitude) {
      dispatch(locationStepOperations.setCoordinates({ lat: subDistrict.latitude, lng: subDistrict.longitude }));
    } else if (district && district.latitude && district.longitude) {
      dispatch(locationStepOperations.setCoordinates({ lat: district.latitude, lng: district.longitude }));
    } else if (city && city.latitude && city.longitude) {
      dispatch(locationStepOperations.setCoordinates({ lat: city.latitude, lng: city.longitude }));
    } else if (selectedProvince && selectedProvince.latitude && selectedProvince.longitude) {
      dispatch(
        locationStepOperations.setCoordinates({ lat: selectedProvince.latitude, lng: selectedProvince.longitude })
      );
    }
  };

  const getLocationWithoutSuggestion = async (city, district = null, subdistrict = null) => {
    try {
      resetSession();
      let searchAddress = `${addressValue} ${selectedProvince.name.split('(')[0]} ${city.name}`;
      if (district) {
        searchAddress = `${searchAddress} ${district.name}`;
      }
      if (subdistrict) {
        searchAddress = `${searchAddress} ${subdistrict.name}`;
      }
      // Usar Geocoder (GeocodeService.Search) para direcciones escritas sin seleccionar sugerencias
      const geocodeOptions = siteCodes.useGmapCode ? { componentRestrictions: { country: siteCodes.useGmapCode } } : {};
      const { results } = await geocode(searchAddress, geocodeOptions);
      // Filtrar solo resultados del pais en cuestion cuando el sitio es Ecuador
      const filterResults = site?.isEcuador
        ? results.filter(result =>
            result?.address_components?.some(
              component =>
                component.types.includes('country') && component.short_name === siteCodes.useGmapCode?.toUpperCase()
            )
          )
        : results;
      let latLng;
      if (filterResults.length > 0) {
        const loc = filterResults[0]?.geometry?.location;
        latLng = loc ? { lat: loc.lat(), lng: loc.lng() } : undefined;
      } else {
        console.log('No se encontraron resultados para la direccion ingresada.');
        return setDefaultLocation(city, district, subdistrict);
      }
      dispatch(locationStepOperations.setCoordinates(latLng));
    } catch {
      setDefaultLocation(city, district, subdistrict);
    } finally {
      resetSession();
    }
  };

  useEffect(() => {
    if (searchWithCep && selectedCity && !loadingDistrict) {
      setAddress(null);
      setAddressValue(null);
      setValue('addressVal', null);
      dispatch(locationStepOperations.setCoordinates({ lat: '', lng: '' }));
      setSearchWithCep(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchWithCep, selectedCity, loadingDistrict]);

  const handleUnSelectSuggestions = async value => {
    setGetSuggestions(false);
    const cepVal = cepvalue;
    const [shortAddress] = address ? address.split(addressSeparator) : '';
    if (addressValue !== shortAddress) {
      if (value) {
        // sendEvent('Principales Modulo2', 'No Sugerencia');
      }
      setIsEdited(true);
      if ((cepVal !== undefined && cepVal === '') || cepVal === undefined) {
        dispatch(locationStepOperations.resetValues());
      }
      setAddress(value);
      if (addressValue && cepVal !== undefined && cepVal !== '') {
        getLocationWithoutSuggestion(selectedCity, selectedProvince, selectedDistrict, selectedSubDistrict);
      }
      // si no llega el valor del campo de direccion reseteo los campos
      if (!value && !isOnCEPView && !cepVal) {
        dispatch(locationStepOperations.resetValues());
      }
    }
  };

  const handleSelectSuggestions = async (value, select = false, fromBrSuggestion = false, address, placeId) => {
    const cepVal = hasCep ? cepvalue : undefined;
    if ((select && cepVal === undefined) || (select && cepVal !== undefined && cepVal === '') || fromBrSuggestion) {
      if (!placeId) {
        handleUnSelectSuggestions(value);
        return false;
      }
      try {
        const details = await getDetails(placeId);
        const filterResults = [details.raw].filter(result =>
          result?.address_components?.some(
            component =>
              component.types.includes('country') && component.short_name === siteCodes.useGmapCode?.toUpperCase()
          )
        );
        const getCoordinates = filterResults[0] ? { lat: details.lat, lng: details.lng } : null;
        if (!getCoordinates) {
          return false;
        }
        if (site.isBrasil) {
          setHasSearchWithCep(true);
          const postalCode = getPostalCodeLongName([details.raw]);
          dispatch(locationStepOperations.resetCepError());
          setLastCepSearched(postalCode);
          setCep(postalCode);
        }
        setGetSuggestions(false);
        setAddress(address);
        setAddressValue(address);
        setValue('addressVal', address);

        if (isAnExcludePortaltoGetLatLng) {
          resetSession();
          return true;
        }
        const dataCoords = await dispatch(locationStepOperations.getLatLngData(getCoordinates.lat, getCoordinates.lng));
        if (dataCoords.data[0] !== undefined) {
          setLocationByCoords(dataCoords.data[0]);
          clearErrors();
          resetSession();
          return true;
        }
        dataCoords.data[0] = [];
        dispatch(locationStepOperations.resetValues());
        resetSession();
        return false;
      } catch {
        resetSession();
        return false;
      }
    } else {
      handleUnSelectSuggestions(value);
      return false;
    }
  };

  const getCep = async () => {
    if (loadingCep) return;
    setCep(cepvalue);
    const dataCoords = await dispatch(locationStepOperations.getCep(cepvalue));
    if (dataCoords !== undefined && dataCoords.data !== undefined) {
      setLocationByCoords(dataCoords.data[0]);
      if (addressValue) {
        setSearchWithCep(true);
      } else {
        dispatch(locationStepOperations.resetCoordinates());
      }
    } else {
      dispatch(locationStepOperations.resetCoordinates());
    }
  };

  const levelDataMap = useMemo(
    () => ({
      province: { data: provinceData, nextLevel: 'cityData', setter: locationStepOperations.setSelectedProvince },
      city: { data: cityData, nextLevel: 'districtData', setter: locationStepOperations.setSelectedCity },
      district: {
        data: districtData,
        nextLevel: 'subDistrictData',
        setter: locationStepOperations.setSelectedDistrict
      },
      subdistrict: { data: subDistrictData, nextLevel: null, setter: locationStepOperations.setSelectedSubDistrict }
    }),
    [provinceData, cityData, districtData, subDistrictData]
  );

  const handleLocationChange = (value, level) => {
    const { data, nextLevel, setter } = levelDataMap[level];
    if (value) {
      const infoLocation = data.find(item => item.location_id === value);
      if (infoLocation) {
        if (nextLevel) {
          dispatch(locationStepOperations.getLocation(infoLocation.location_id, nextLevel));
        }
        dispatch(setter(infoLocation, 'cleanValues'));

        // Asegurar que setValue se ejecute inmediatamente
        if (level === 'province') {
          setValue('provinceVal', value);
          clearErrors('provinceVal');
        } else if (level === 'city') {
          setValue('cityVal', value);
          clearErrors('cityVal');
          getLocationWithoutSuggestion(infoLocation);
        } else if (level === 'district') {
          setValue('districtVal', value);
          clearErrors('districtVal');
          getLocationWithoutSuggestion(selectedCity, infoLocation);
        } else if (level === 'subdistrict') {
          setValue('subDistrictVal', value);
          clearErrors('subDistrictVal');
          getLocationWithoutSuggestion(selectedCity, selectedDistrict, infoLocation);
        }
      }
    } else {
      dispatch(setter('', 'cleanValues'));

      // Limpiar valores en RHF cuando se resetea
      if (level === 'province') {
        setValue('provinceVal', '');
        setValue('cityVal', '');
        setValue('districtVal', '');
        clearErrors(['provinceVal', 'cityVal', 'districtVal']);
      } else if (level === 'city') {
        setValue('cityVal', '');
        setValue('districtVal', '');
        clearErrors(['cityVal', 'districtVal']);
      } else if (level === 'district') {
        setValue('districtVal', '');
        clearErrors('districtVal');
      }
    }
    setIsEdited(true);
  };

  const onKeyDown = evt => {
    const code = evt.which ? evt.which : evt.keyCode;
    const dashCodes = [109, 189];
    if (!(isSpecialKey(code) || dashCodes.includes(code) || isKeyCodeNumber(code) || isKeyCodeNumpadNumber(code))) {
      evt.preventDefault();
    }
  };

  const handleSelect = (addr, placeId, placeData, fromBrSuggestion = false) => {
    const address =
      // placeData?.formattedSuggestion?.mainText ||
      placeData?.structured_formatting?.main_text || placeData?.description || addr;
    if (fromBrSuggestion) {
      return handleSelectSuggestions(addr, false, true, address, placeId);
    }
    if (placeId) {
      handleSelectSuggestions(addr, true, false, address, placeId);
    } else {
      handleUnSelectSuggestions(addr);
    }
  };

  const onCepChange = e => {
    const value = e.target.value;
    if (value === '') {
      setCep('');
      return;
    }
    if (isValidFormatCep(value)) {
      setCep(value);
    } else {
      return false;
    }
    setCep(value);
  };

  const onCepSearch = async () => {
    // valido que el cep sea distinto al ultimo buscado para evitar llamadas innecesarias
    if (!cepvalue) {
      showErrorSnackbar('cepValidationEmpty', t('professionalPubli.location.cep.snackbar'));
    }
    if (cepvalue === lastCepSearched) return;
    setLastCepSearched(cepvalue);
    setHasSearchWithCep(true);
    const valid = await trigger('cep', { shouldFocus: true });
    if (valid) {
      getCep();
    }
  };

  if (loadError) {
    return 'Error loading maps';
  }
  if (!isLoaded) {
    return 'Loading Maps';
  }

  return site.isBrasil ? (
    <LocationCombosBR
      cepDefault={cepDefault}
      onKeyDown={onKeyDown}
      onCepChange={onCepChange}
      watch={watch}
      cepError={cepError}
      cepNotFound={cepNotFound}
      errors={errors}
      breakMobile={breakMobile}
      onCepSearch={onCepSearch}
      getValues={getValues}
      setAddressComplement={setAddressComplement}
      setGetSuggestions={setGetSuggestions}
      setAddressValidation={setAddressValidation}
      setAddressValue={setAddressValue}
      setValue={setValue}
      handleSelect={handleSelect}
      handleOnError={handleOnError}
      predictions={predictions}
      placesLoading={placesLoading}
      placesReady={placesReady}
      onPlacesSearch={searchPlaces}
      addressValue={addressValue}
      addressValidation={addressValidation}
      handleSelectSuggestions={handleSelectSuggestions}
      register={register}
      postalCode={postalCode}
      getSuggestions={getSuggestions}
      hasCoordinates={hasCoordinates}
      address={address}
      isMobile={isMobile}
      provinceData={provinceData}
      cityData={cityData}
      districtData={districtData}
      subDistrictData={subDistrictData}
      selectedProvince={selectedProvince}
      selectedCity={selectedCity}
      selectedDistrict={selectedDistrict}
      selectedSubDistrict={selectedSubDistrict}
      siteCodes={siteCodes}
      handleLocationChange={handleLocationChange}
      cep={cep}
      addressComplement={addressComplement}
      dispatch={dispatch}
      locationStepOperations={locationStepOperations}
      setIsOnCEPV={setIsOnCEPV}
      isOnCEPView={isOnCEPView}
      hasSearchWithCep={hasSearchWithCep}
    />
  ) : (
    <LocationCombosCommon
      errors={errors}
      getValues={getValues}
      setGetSuggestions={setGetSuggestions}
      setAddressValidation={setAddressValidation}
      setAddressValue={setAddressValue}
      setValue={setValue}
      handleSelect={handleSelect}
      handleOnError={handleOnError}
      predictions={predictions}
      placesLoading={placesLoading}
      placesReady={placesReady}
      onPlacesSearch={searchPlaces}
      addressValue={addressValue}
      addressValidation={addressValidation}
      handleSelectSuggestions={handleSelectSuggestions}
      register={register}
      postalCode={postalCode}
      getSuggestions={getSuggestions}
      hasCoordinates={hasCoordinates}
      address={address}
      isMobile={isMobile}
      provinceData={provinceData}
      cityData={cityData}
      districtData={districtData}
      subDistrictData={subDistrictData}
      selectedProvince={selectedProvince}
      selectedCity={selectedCity}
      selectedDistrict={selectedDistrict}
      selectedSubDistrict={selectedSubDistrict}
      siteCodes={siteCodes}
      handleLocationChange={handleLocationChange}
    />
  );
};
